import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, MagnifyingGlass, Trash } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { API, authCfg, pad, CleanerSelect, PdfPrintButtons, useRowSelection, SelectionBar, JobFilterChecks, NeedsCheckTag } from './CleaningScheduleShared';
import { CleaningReports } from './CleaningReports';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TYPE = { roof: { history: 'roof_terrace', job: 'Roof', title: 'Annual Roof Cleaning Schedule' }, windows: { history: 'windows', job: 'Windows', title: 'Annual Window Cleaning Schedule' } };
const num = (v) => (v === '' || v == null ? null : parseFloat(v));

const saveEntry = async ({ kind, target, f, cleaners }) => {
  const cl = cleaners.find((c) => c.cleaner_id === f.cleaner_id);
  const it = target.item;
  if (!it) {
    if (target.row.kind === 'building') {
      return axios.post(`${API}/cleaning-history`, { building_id: target.row.ref_id, date: f.date, cleaner_id: f.cleaner_id, employee_name: cl?.name || '', cleaning_type: TYPE[kind].history, cleaner_payment: num(f.cost), hours_worked: num(f.hours), notes: f.notes }, authCfg());
    }
    const c = { cleaner_id: f.cleaner_id, hours_worked: num(f.hours) || 0 };
    if (f.cost !== '') c.cleaner_earned = parseFloat(f.cost);
    return axios.post(`${API}/cleaning/jobs`, { client_id: target.row.ref_id, date: f.date, cleaners: [c], notes: f.notes, job_type: TYPE[kind].job }, authCfg());
  }
  if (it.source === 'history') {
    const body = { date: f.date, cleaner_id: f.cleaner_id, employee_name: cl?.name || it.cleaner, notes: f.notes, hours_worked: num(f.hours) };
    if (!it.paid) body.cleaner_payment = num(f.cost) ?? 0;
    return axios.put(`${API}/cleaning-history/${it.id}`, body, authCfg());
  }
  const list = it.job_cleaners.map((x, i) => (i === it.cleaner_index ? { cleaner_id: f.cleaner_id, hours_worked: num(f.hours) || 0, cleaner_earned: num(f.cost) || 0 } : x));
  return axios.put(`${API}/cleaning/jobs/${it.id}`, { date: f.date, notes: f.notes, cleaners: list }, authCfg());
};

const deleteEntry = async (it) => {
  if (it.source === 'history') return axios.delete(`${API}/cleaning/schedule/history-entries/${it.id}`, authCfg());
  if (it.job_cleaners.length > 1) return axios.put(`${API}/cleaning/jobs/${it.id}`, { cleaners: it.job_cleaners.filter((_, i) => i !== it.cleaner_index) }, authCfg());
  return axios.delete(`${API}/cleaning/jobs/${it.id}`, authCfg());
};

const Field = ({ label, children }) => <label className="block text-xs text-zinc-500">{label}<div className="mt-1">{children}</div></label>;

const EntryDialog = ({ kind, target, cleaners, el, onClose, onSaved, isAdmin }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!target) return;
    const it = target.item;
    setF(it ? { date: it.date, cleaner_id: it.cleaner_id, hours: it.hours ?? '', cost: it.cost ?? '', notes: it.notes || '' } : { date: target.minDate, cleaner_id: '', hours: '', cost: '', notes: '' });
  }, [target]);
  if (!target) return null;
  const it = target.item;
  const canDelete = it && !it.paid && (it.source === 'history' || isAdmin || it.job_cleaners.length > 1);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); setConfirm(false); }
  };
  const save = () => {
    if (!f.date || !f.cleaner_id) { toast.error(el ? 'Συμπληρώστε ημερομηνία και καθαρίστρια' : 'Date and cleaner are required'); return; }
    run(() => saveEntry({ kind, target, f, cleaners }), el ? 'Αποθηκεύτηκε' : 'Saved');
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" aria-describedby={undefined} data-testid="annual-entry-dialog">
        <DialogHeader><DialogTitle className="text-base">{target.row.name} — {TYPE[kind].job} · {it ? (el ? 'Επεξεργασία' : 'Edit') : MONTHS[target.month]}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label={el ? 'Ημερομηνία' : 'Date'}><Input type="date" min={it ? undefined : target.minDate} max={it ? undefined : target.maxDate} value={f.date || ''} onChange={set('date')} className="rounded-none" data-testid="annual-entry-date" /></Field>
          <Field label={el ? 'Καθαρίστρια' : 'Cleaner'}>
            <select value={f.cleaner_id || ''} onChange={set('cleaner_id')} disabled={it?.paid} className="w-full h-9 border border-zinc-300 px-2 text-sm" data-testid="annual-entry-cleaner">
              <option value="">—</option>
              {cleaners.map((c) => <option key={c.cleaner_id} value={c.cleaner_id}>{c.name}</option>)}
            </select></Field>
          <Field label={el ? 'Ώρες εργασίας' : 'Hours'}><Input type="number" step="0.25" value={f.hours} onChange={set('hours')} className="rounded-none" data-testid="annual-entry-hours" /></Field>
          <Field label={el ? 'Κόστος καθαρίστριας (€)' : 'Cleaner cost (€)'}><Input type="number" step="0.01" value={f.cost} onChange={set('cost')} disabled={it?.paid} className="rounded-none" data-testid="annual-entry-cost" /></Field>
          <div className="col-span-2"><Field label={el ? 'Σημειώσεις' : 'Notes'}><Input value={f.notes || ''} onChange={set('notes')} className="rounded-none" data-testid="annual-entry-notes" /></Field></div>
          {it?.paid && <p className="col-span-2 text-[11px] text-amber-700" data-testid="annual-entry-paid-note">{el ? 'Η εργασία έχει ήδη εξοφληθεί στην καθαρίστρια — το κόστος και η διαγραφή είναι κλειδωμένα.' : 'Already paid to the cleaner — cost and delete are locked.'}</p>}
        </div>
        <DialogFooter className="sm:justify-between">
          <div>{canDelete && <Button variant="outline" className="rounded-none border-red-300 text-red-600 hover:bg-red-50" onClick={() => setConfirm(true)} data-testid="annual-entry-delete"><Trash size={14} className="mr-1" />{el ? 'Διαγραφή' : 'Delete'}</Button>}</div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="annual-entry-cancel">{el ? 'Ακύρωση' : 'Cancel'}</Button>
            <Button className="rounded-none bg-zinc-950" disabled={busy} onClick={save} data-testid="annual-entry-save">{el ? 'Αποθήκευση' : 'Save'}</Button>
          </div>
        </DialogFooter>
        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent className="rounded-none" data-testid="annual-entry-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{el ? 'Διαγραφή καταχώρισης;' : 'Delete entry?'}</AlertDialogTitle>
              <AlertDialogDescription>{it?.date} · {it?.cleaner} · €{Number(it?.cost || 0).toFixed(2)}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{el ? 'Ακύρωση' : 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction className="rounded-none bg-red-600 hover:bg-red-700" onClick={(e) => { e.preventDefault(); run(() => deleteEntry(it), el ? 'Διαγράφηκε' : 'Deleted'); }} data-testid="annual-entry-delete-ok">{el ? 'Διαγραφή' : 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

const MonthCell = ({ items, onAdd, onEdit, testId }) => (
  <td className="px-1.5 py-1 border-l border-zinc-200 align-middle whitespace-nowrap cursor-pointer hover:bg-amber-50" onClick={onAdd} data-testid={testId}>
    <div className="flex flex-nowrap items-baseline gap-x-2 min-h-[16px]">
      {items.map((it, i) => (
        <button key={`${it.id}-${it.cleaner_index ?? 0}-${i}`} onClick={(e) => { e.stopPropagation(); onEdit(it); }} className="inline-flex items-baseline gap-1 hover:underline" data-testid="annual-item">
          <span className="font-mono text-[12px] font-semibold text-zinc-900">{it.date.slice(8, 10)}/{it.date.slice(5, 7)}</span>
          <span className="text-[10px] text-zinc-500">{it.cleaner || '—'}</span>
        </button>
      ))}
    </div>
  </td>
);

const AnnualTable = ({ rows, sel, openAdd, openEdit, loading, el }) => (
  <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="annual-table-wrap">
    <table className="w-full text-xs border-collapse" data-testid="annual-table">
      <thead><tr className="bg-zinc-900 text-white text-[10px] uppercase tracking-wider">
        <th className="px-1 py-1.5 w-6" />
        <th className="px-1.5 py-1.5 text-right font-semibold w-8">No.</th>
        <th className="text-left px-2 py-1.5 font-semibold min-w-[110px] sm:min-w-[160px]">Building / Client</th>
        {MONTHS.map((m) => <th key={m} className="text-left px-1.5 py-1.5 font-semibold border-l border-zinc-700 min-w-[64px]" data-testid={`annual-head-${m.toLowerCase()}`}>{m}</th>)}
      </tr></thead>
      <tbody>
        {loading && <tr><td colSpan={15} className="py-10 text-center text-zinc-400">…</td></tr>}
        {!loading && rows.length === 0 && <tr><td colSpan={15} className="py-10 text-center text-zinc-400" data-testid="annual-empty">{el ? 'Δεν υπάρχουν γραμμές για τα επιλεγμένα φίλτρα' : 'No rows for the selected filters'}</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.row_key} className="border-t border-zinc-200 odd:bg-white even:bg-zinc-50/70" data-testid={`annual-row-${r.row_key}`}>
            <td className="px-1 py-1 text-center"><input type="checkbox" checked={sel.selected.has(r.row_key)} onChange={() => sel.toggle(r.row_key)} className="accent-zinc-900" data-testid={`annual-select-${r.row_key}`} /></td>
            <td className="px-1.5 py-1 text-right font-mono text-[11px] text-zinc-500" data-testid={`annual-no-${r.row_key}`}>{i + 1}</td>
            <td className="px-2 py-1 font-semibold text-zinc-900 whitespace-nowrap" title={r.name}>
              <span className="inline-block align-middle max-w-[120px] sm:max-w-[220px] truncate">{r.name}</span>
              {!r.has_entries && <NeedsCheckTag testId={`annual-needs-check-${r.row_key}`} />}
            </td>
            {r.months.map((cell, m) => <MonthCell key={m} items={cell} onAdd={() => openAdd(r, m)} onEdit={(it) => openEdit(r, it)} testId={`annual-cell-${r.row_key}-m${m + 1}`} />)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AnnualView = ({ kind, el, cleaners }) => {
  const { isAdmin } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [cleanerId, setCleanerId] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const sel = useRowSelection(data?.rows, search);
  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/cleaning/annual?kind=${kind}&year=${year}&cleaner_id=${cleanerId}`, authCfg())).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  }, [kind, year, cleanerId]);
  useEffect(() => { setData(null); load(); }, [load]);
  const openAdd = (row, m) => {
    const last = new Date(year, m + 1, 0).getDate();
    setTarget({ row, month: m, minDate: `${year}-${pad(m + 1)}-01`, maxDate: `${year}-${pad(m + 1)}-${pad(last)}` });
  };
  const pdf = (paper) => () => sel.printParams({ kind, year, cleaner_id: cleanerId, paper, lang: el ? 'el' : 'en' });
  return (
    <div data-testid={`annual-${kind}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="annual-toolbar">
        <div className="flex items-center gap-1">
          <button onClick={() => setYear(year - 1)} className="h-8 w-8 border border-zinc-300 bg-white flex items-center justify-center hover:bg-zinc-50" data-testid="annual-prev-year"><CaretLeft size={14} /></button>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} className="h-8 w-20 rounded-none text-sm font-bold" data-testid="annual-year-input" />
          <button onClick={() => setYear(year + 1)} className="h-8 w-8 border border-zinc-300 bg-white flex items-center justify-center hover:bg-zinc-50" data-testid="annual-next-year"><CaretRight size={14} /></button>
        </div>
        <CleanerSelect cleaners={cleaners} value={cleanerId} onChange={setCleanerId} el={el} testId="annual-cleaner-select" />
        <div className="relative"><MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={el ? 'Αναζήτηση κτιρίου / πελάτη…' : 'Search building / client…'} className="h-8 w-48 rounded-none pl-7 text-xs" data-testid="annual-search" /></div>
        <JobFilterChecks sel={sel} el={el} />
        <div className="ml-auto flex flex-wrap gap-1.5">
          <PdfPrintButtons label={`${TYPE[kind].title} (A3)`} path="/cleaning/annual/pdf" params={pdf('a3')} testId={`annual-${kind}-export-a3`} />
          <PdfPrintButtons label="A4" path="/cleaning/annual/pdf" params={pdf('a4')} testId={`annual-${kind}-export-a4`} />
        </div>
      </div>
      <SelectionBar sel={sel} el={el} />
      <h3 className="text-sm font-bold text-zinc-900 mb-1" data-testid="annual-title">{TYPE[kind].title} — {year}</h3>
      <AnnualTable rows={sel.rows} sel={sel} loading={!data} el={el} openAdd={openAdd} openEdit={(row, item) => setTarget({ row, item })} />
      <EntryDialog kind={kind} target={target} cleaners={cleaners} el={el} isAdmin={isAdmin} onClose={() => setTarget(null)} onSaved={() => { setTarget(null); load(); }} />
    </div>
  );
};

export const CleaningAnnual = ({ kind, el, cleaners, rowOptions }) => {
  const [mode, setMode] = useState('annual');
  return (
    <div>
      <div className="flex gap-0 border border-zinc-300 bg-white w-fit mb-3" data-testid={`annual-mode-switch-${kind}`}>
        {[['annual', `${TYPE[kind].title}`], ['range', 'Custom Date Range']].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} className={`px-3 py-1 text-[11px] font-semibold border-r border-zinc-200 last:border-r-0 transition-colors ${mode === k ? 'bg-amber-100 text-amber-900' : 'text-zinc-600 hover:bg-zinc-50'}`} data-testid={`annual-mode-${k}`}>{l}</button>
        ))}
      </div>
      {mode === 'annual' ? <AnnualView kind={kind} el={el} cleaners={cleaners} /> : <CleaningReports kind={kind} el={el} cleaners={cleaners} rowOptions={rowOptions} />}
    </div>
  );
};
